import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLogin from '../components/admin/AdminLogin';
import AdminNav from '../components/admin/AdminNav';
import { getSesion } from '../utils/sesionAdmin';
import '../styles/admin.css';
import { ADMIN_API } from '../utils/adminApi';

const POLL_MS = 2000;

// A diferencia de leads y finanzas, esta vista NO bloquea móvil: el punto de
// tener el botón de envío acá es poder mandar los lotes desde el teléfono.
const AdminEmailPage = () => {
  const [stage, setStage] = useState('checking'); // 'checking' | 'login' | 'viewer' | 'error'
  const [data, setData] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [reloading, setReloading] = useState(false);
  const [job, setJob] = useState(null);
  const [aviso, setAviso] = useState('');
  const [sesion, setSesion] = useState(null);
  // Lectura del copy: qué campaña está abierta y su contenido. Se pide al abrir
  // y no en el resumen, para no traer 23 correos completos en cada carga.
  const [copyAbierto, setCopyAbierto] = useState(null);
  const [copy, setCopy] = useState(null);
  const [copyCargando, setCopyCargando] = useState(false);
  const [copyError, setCopyError] = useState('');
  const pollRef = useRef(null);

  const fetchResumen = useCallback(async () => {
    try {
      // La sesión primero: un coach no debe ni disparar la petición del panel.
      const ses = await getSesion();
      setSesion(ses);
      if (ses.rol !== 'admin') {
        setStage('denegado');
        return;
      }
      const res = await axios.get(`${ADMIN_API}/api/admin/email/resumen`, {
        withCredentials: true,
      });
      setData(res.data);
      setStage('viewer');
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
      } else if (err?.response?.status === 403) {
        setStage('denegado');
      } else {
        setErrMsg(err?.response?.data?.error || 'No se pudo conectar con el servidor.');
        setStage('error');
      }
    }
  }, []);

  const reload = async () => {
    setReloading(true);
    await fetchResumen();
    setReloading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${ADMIN_API}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Ignorar error de logout: igual mostramos el login
    }
    setData(null);
    setStage('login');
  };

  const detenerPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const seguirJob = useCallback((jobId) => {
    detenerPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${ADMIN_API}/api/admin/email/job/${jobId}`, {
          withCredentials: true,
        });
        setJob(res.data);
        if (res.data.estado !== 'corriendo') {
          detenerPolling();
          fetchResumen();
        }
      } catch (err) {
        detenerPolling();
        if (err?.response?.status === 404) {
          setAviso('Se perdió el seguimiento del envío (el servidor se reinició). Recarga para ver el estado real: los correos ya enviados quedaron registrados y no se repiten.');
          setJob(null);
        }
      }
    }, POLL_MS);
  }, [detenerPolling, fetchResumen]);

  useEffect(() => {
    fetchResumen();
    return detenerPolling;
  }, [fetchResumen, detenerPolling]);

  // Abre o cierra el correo de una campaña. Segundo clic en la misma, cierra.
  const verCopy = async (key) => {
    if (copyAbierto === key) {
      setCopyAbierto(null);
      return;
    }
    setCopyAbierto(key);
    setCopy(null);
    setCopyError('');
    setCopyCargando(true);
    try {
      const { data: d } = await axios.get(`${ADMIN_API}/api/admin/email/copy/${key}`, { withCredentials: true });
      setCopy(d);
    } catch (e) {
      setCopyError(e?.response?.data?.error || 'No se pudo leer el contenido de esa campaña.');
    } finally {
      setCopyCargando(false);
    }
  };

  const enviarLote = async (campana) => {
    setAviso('');
    let previo;
    try {
      const res = await axios.post(
        `${ADMIN_API}/api/admin/email/enviar`,
        { campaign: campana.key, dryRun: true },
        { withCredentials: true },
      );
      previo = res.data;
    } catch (err) {
      setAviso(err?.response?.data?.error || 'No se pudo calcular el lote.');
      return;
    }

    if (!previo.pendientes) {
      setAviso(`No quedan pendientes en ${campana.key}.`);
      return;
    }

    const minutos = Math.ceil((previo.loteSeria * 1.2) / 60);
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `${campana.key}\n\n`
      + `Quedan ${previo.pendientes} pendientes.\n`
      + `Este lote manda ${previo.loteSeria} correos (unos ${minutos} min).\n\n`
      + '¿Confirmas el envío?',
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        `${ADMIN_API}/api/admin/email/enviar`,
        { campaign: campana.key, dryRun: false },
        { withCredentials: true },
      );
      const nuevoJob = {
        id: res.data.jobId,
        campaign: campana.key,
        estado: 'corriendo',
        total: previo.loteSeria,
        enviados: 0,
        fallidos: 0,
      };
      setJob(nuevoJob);
      seguirJob(res.data.jobId);
    } catch (err) {
      if (err?.response?.status === 409) {
        setAviso('Ya hay un envío corriendo. Espera a que termine.');
      } else {
        setAviso(err?.response?.data?.error || 'No se pudo iniciar el envío.');
      }
    }
  };

  if (stage === 'checking') {
    return (
      <div className="au-admin au-admin-shell">
        <div className="au-week-empty">&gt;&gt; Conectando…</div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="au-admin au-admin-shell">
        <div className="au-week-empty">
          &gt;&gt; {errMsg}{' '}
          <button
            type="button"
            className="au-admin-btn"
            onClick={() => { setStage('checking'); fetchResumen(); }}
          >
            REINTENTAR
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'denegado') {
    return (
      <>
        <div className="au-admin au-admin-nav-wrap">
          <AdminNav actual="email" rol={sesion?.rol} />
        </div>
        <div className="au-admin au-admin-shell">
          <div className="au-week-empty">&gt;&gt; No tienes acceso a esta sección.</div>
        </div>
      </>
    );
  }

  if (stage === 'login') {
    return <AdminLogin onSuccess={() => { setStage('checking'); fetchResumen(); }} />;
  }

  const grupos = data?.grupos || [];
  const automaticos = data?.automaticos || [];
  const base = data?.base || { desuscritos: 0, rebotados: 0, recientes: null };
  const recientes = base.recientes || { enviados: 0, entregados: 0 };

  // Dos estados distintos, y confundirlos hace que la alerta se vuelva ruido:
  // - Nunca llegó una entrega: no es una falla, es que el webhook es nuevo o
  //   todavía no sale un correo. Aviso neutro, no alarma.
  // - Antes entregaba y ahora no: eso sí es una caída. Alarma.
  const sinEventosAun = (base.entregadosTotales || 0) === 0;
  const medicionCaida = !sinEventosAun
    && recientes.enviados >= 20
    && recientes.entregados === 0;

  return (
    <div className="au-admin">
      <AdminNav actual="email" rol={sesion?.rol} />
      <div className="au-admin-shell">
        <div className="au-admin-header">
          <div className="brand">
            EMAIL
            <span className="subtitle mono muted"> · campañas y envíos</span>
          </div>
          <div className="au-admin-header-actions">
            <button type="button" className="au-admin-btn" onClick={reload} disabled={reloading}>
              {reloading ? 'ACTUALIZANDO…' : 'ACTUALIZAR'}
            </button>
            <button type="button" className="au-admin-btn" onClick={logout}>SALIR</button>
          </div>
        </div>

        {medicionCaida && (
          <div className="au-admin-alerta">
            &gt;&gt; El webhook de Resend dejó de registrar entregas: {recientes.enviados} correos
            enviados en los últimos 7 días y ninguna entrega confirmada.
            Revisa que <span className="mono">RESEND_WEBHOOK_SECRET</span> siga configurado en Render
            y que el endpoint apunte a <span className="mono">/api/resend/webhook</span>.
          </div>
        )}

        {sinEventosAun && (
          <div className="au-admin-nota">
            &gt;&gt; Todavía no ha llegado ningún evento de entrega. Las columnas de entregados,
            aperturas y clicks se llenan con los correos que salgan desde ahora; los envíos
            anteriores a la configuración del webhook no se pueden recuperar.
          </div>
        )}

        {aviso && <div className="au-admin-alerta">&gt;&gt; {aviso}</div>}

        {job && (
          <div className="au-admin-job">
            <div className="mono upper">
              {job.estado === 'corriendo' && `Enviando ${job.campaign}…`}
              {job.estado === 'terminado' && `Listo: ${job.campaign}`}
              {job.estado === 'error' && `Error en ${job.campaign}`}
            </div>
            <div className="au-admin-job-barra">
              <div
                className="au-admin-job-progreso"
                style={{ width: `${job.total ? Math.round((job.enviados / job.total) * 100) : 0}%` }}
              />
            </div>
            <div className="mono muted">
              {job.enviados} / {job.total} enviados
              {job.fallidos > 0 && ` · ${job.fallidos} fallidos`}
              {job.error && ` · ${job.error}`}
            </div>
          </div>
        )}

        <div className="au-admin-email-base mono muted">
          Base: {base.desuscritos} desuscritos · {base.rebotados} rebotados
          {' · '}
          últimos 7 días: {recientes.enviados} enviados, {recientes.entregados} entregados
        </div>

        <h2 className="au-admin-email-titulo mono upper">Bienvenidas de otros orígenes</h2>
        <p className="au-admin-grupo-detalle muted">Split y calculadora. Salen dentro de la hora en que el lead deja su correo, sin ventana horaria a propósito. Todavía no tienen secuencia detrás.</p>
        <div className="au-admin-email-auto">
          {automaticos.map((a) => (
            <div key={a.key} className="au-admin-email-auto-item">
              <span className={`au-admin-punto${a.activo ? ' is-activo' : ''}`} />
              <span className="mono">{a.etiqueta || a.key}</span>
              <span className="mono muted">
                {a.enviados} enviados · {a.ultimoEnvio ? `último ${a.ultimoEnvio}` : 'sin envíos'}
              </span>
            </div>
          ))}
        </div>

        {grupos.map((g) => (
          <section key={g.key} className="au-admin-grupo">
            <h2 className="au-admin-email-titulo mono upper">{g.titulo}</h2>
            <p className="au-admin-grupo-detalle muted">{g.detalle}</p>
            <div className="au-admin-grupo-totales mono muted">
              {g.totales.enviados} enviados · {g.totales.entregados} entregados
              {' · '}{g.totales.abiertos} abiertos · {g.totales.clicks} clicks
              {g.totales.rebotes > 0 && ` · ${g.totales.rebotes} rebotes`}
            </div>

            <div className="au-admin-tabla-wrap">
              <table className="au-admin-tabla">
                <thead>
                  <tr>
                    <th>Correo</th>
                    <th>Env.</th>
                    <th>Entr.</th>
                    <th>Abier.</th>
                    <th>Clicks</th>
                    <th>Reb.</th>
                    <th>Último</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.correos.map((c) => (
                    <React.Fragment key={c.key}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="au-admin-email-nombre"
                            onClick={() => verCopy(c.key)}
                            title="Ver el correo tal como se manda"
                          >
                            <span className="mono">{c.etiqueta}</span>
                            <span className="au-admin-email-lupa mono">
                              {copyAbierto === c.key ? '\u25be' : '\u25b8'}
                            </span>
                          </button>
                          {/* La clave va visible porque es lo que aparece como
                              origen del lead (utm_campaign). Sin esto hay que
                              adivinar que "015-idea-de-fondo" es "paso 1". */}
                          <div className="mono muted au-admin-email-clave">{c.key}</div>
                          <div className="muted au-admin-email-asunto">{c.asunto}</div>
                        </td>
                        <td className="mono">{c.enviados}</td>
                        <td className="mono">{c.entregados}</td>
                        <td className="mono">{c.abiertos}</td>
                        <td className="mono">{c.clicks}</td>
                        <td className="mono">{c.rebotes}</td>
                        <td className="mono muted">{c.ultimoEnvio || '\u2014'}</td>
                        <td>
                          {c.disparable ? (
                            <button
                              type="button"
                              className="au-admin-btn"
                              onClick={() => enviarLote(c)}
                              disabled={Boolean(job && job.estado === 'corriendo')}
                            >
                              ENVIAR LOTE
                            </button>
                          ) : (
                            <span className="mono muted au-admin-email-solo-cli">
                              {c.automatica ? 'auto' : 'solo CLI'}
                            </span>
                          )}
                        </td>
                      </tr>
                      {copyAbierto === c.key && (
                        <tr className="au-admin-copy-fila">
                          <td colSpan={8}>
                            {copyCargando && <div className="mono muted">cargando el correo...</div>}
                            {copyError && <div className="mono au-admin-copy-error">{copyError}</div>}
                            {copy && copy.key === c.key && (
                              <div className="au-admin-copy">
                                <div className="au-admin-copy-meta mono muted">
                                  <div>De: {copy.remitente}</div>
                                  <div>Asunto: {copy.asunto}</div>
                                  {copy.preview && <div>Preview: {copy.preview}</div>}
                                  {/* Cómo se ve este correo en el origen de un
                                      lead, para poder cruzarlo sin adivinar. */}
                                  <div>
                                    Origen del lead: email · {copy.grupo} · {copy.key.toLowerCase()}
                                  </div>
                                </div>
                                <pre className="au-admin-copy-texto">{copy.texto}</pre>
                                <div className="au-admin-copy-links mono">
                                  {copy.links.length > 0 ? (
                                    <>
                                      <div className="muted">A d\u00f3nde lleva:</div>
                                      {copy.links.map((l) => (
                                        <div key={l} className="au-admin-copy-link">{l}</div>
                                      ))}
                                    </>
                                  ) : (
                                    <div className="au-admin-copy-error">
                                      Este correo no tiene ning\u00fan link: no lleva a ninguna parte.
                                    </div>
                                  )}
                                </div>
                                {copy.reglas && (
                                  <div className="au-admin-copy-reglas mono muted">
                                    Sale solo
                                    {copy.reglas.tipo ? ` a los leads de tipo "${copy.reglas.tipo}"` : ' a toda la base'}
                                    {copy.reglas.requiere ? ` que ya recibieron ${copy.reglas.requiere}` : ''}
                                    {copy.reglas.diasDesdeUltimo ? `, con ${copy.reglas.diasDesdeUltimo} d\u00eda(s) desde su \u00faltimo correo` : ''}
                                    {copy.reglas.limite ? `, hasta ${copy.reglas.limite} por lote` : ''}.
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AdminEmailPage;
